/**
 * The "YOUR LOGO" placeholder — what the sample phone shows in the logo's spot
 * before the operator has given us one (spec `onboarding-round-2` §2.3).
 *
 * It exists so the preview NEVER shows the Quotologies logo on a stranger's
 * brand. Before this, `useSampleProps` fell back to `/logo-quotologies.png`,
 * which put the operator's own wordmark on every screen of every new brand's
 * walk until the logo step. The placeholder is preview-only: `payload.ts`
 * reads `state.logoDataUrl`, which this never touches, so an untouched walk
 * still ships `logoDataUrl: ""` (pinned in payload.test.ts).
 *
 * Two renderings of the same picture. `logoPlaceholderSvgDataUrl` is pure and
 * unit-tested in node — a transparent 3:1 box, a dashed rounded frame, the two
 * words centred in a bold letter-spaced sans. `drawLogoPlaceholder` paints the
 * same thing to a canvas PNG (the shape `logoDataUrl` already expects, and what
 * the renderer's `<Img>` is proven on) and falls back to the SVG on any
 * failure. One draw per session — the result is memoised at module level.
 */

export const LOGO_PLACEHOLDER_W = 1200;
export const LOGO_PLACEHOLDER_H = 400;

const LABEL = "YOUR LOGO";
const INSET = 12;
const RADIUS = 40;
const STROKE = "rgba(255,255,255,0.55)";
const STROKE_W = 6;
const DASH: readonly [number, number] = [28, 18];
const INK = "rgba(255,255,255,0.85)";
const FONT_PX = 96;
const TRACKING_PX = 14;
const FONT_STACK = '"Helvetica Neue", Helvetica, Arial, sans-serif';

/** The pure rendering — an inline SVG data URL. Always available. */
export function logoPlaceholderSvgDataUrl(): string {
  const W = LOGO_PLACEHOLDER_W;
  const H = LOGO_PLACEHOLDER_H;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">` +
    `<rect x="${INSET}" y="${INSET}" width="${W - INSET * 2}" height="${H - INSET * 2}" rx="${RADIUS}" ry="${RADIUS}" ` +
    `fill="none" stroke="${STROKE}" stroke-width="${STROKE_W}" stroke-dasharray="${DASH[0]} ${DASH[1]}"/>` +
    `<text x="${W / 2}" y="${H / 2}" text-anchor="middle" dominant-baseline="central" ` +
    `font-family="${FONT_STACK.replace(/"/g, "&quot;")}" font-size="${FONT_PX}" font-weight="700" ` +
    `letter-spacing="${TRACKING_PX}" fill="${INK}">${LABEL}</text>` +
    `</svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

let drawn: Promise<string> | null = null;
let drawnUrl: string | null = null;

/**
 * The canvas PNG of the same picture, memoised. Never rejects: anything that
 * goes wrong (no `document`, no 2D context, a tainted-canvas export) yields the
 * SVG data URL instead, so the phone always has SOMETHING in the logo's spot.
 */
export function drawLogoPlaceholder(): Promise<string> {
  if (!drawn) {
    drawn = Promise.resolve()
      .then(paint)
      .catch(() => logoPlaceholderSvgDataUrl())
      .then((url) => {
        drawnUrl = url;
        return url;
      });
  }
  return drawn;
}

/** The drawn PNG if a draw has already finished this session, else null —
 *  so a second mount can start on the PNG instead of the SVG-then-swap. */
export function cachedLogoPlaceholder(): string | null {
  return drawnUrl;
}

function paint(): string {
  if (typeof document === "undefined") throw new Error("drawLogoPlaceholder requires a browser");
  const W = LOGO_PLACEHOLDER_W;
  const H = LOGO_PLACEHOLDER_H;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("drawLogoPlaceholder: 2D canvas context unavailable");

  ctx.clearRect(0, 0, W, H);

  // The dashed rounded frame.
  ctx.strokeStyle = STROKE;
  ctx.lineWidth = STROKE_W;
  ctx.setLineDash([...DASH]);
  roundedRect(ctx, INSET, INSET, W - INSET * 2, H - INSET * 2, RADIUS);
  ctx.stroke();
  ctx.setLineDash([]);

  // The label, letter-spaced by hand so it does not depend on
  // `ctx.letterSpacing` (Chrome 99+, absent elsewhere): each glyph is measured
  // and advanced by its width plus the tracking, the run centred as a whole.
  ctx.fillStyle = INK;
  ctx.font = `700 ${FONT_PX}px ${FONT_STACK}`;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  const glyphs = [...LABEL];
  const widths = glyphs.map((g) => ctx.measureText(g).width);
  const run = widths.reduce((a, b) => a + b, 0) + TRACKING_PX * (glyphs.length - 1);
  let x = (W - run) / 2;
  glyphs.forEach((g, i) => {
    ctx.fillText(g, x, H / 2);
    x += widths[i]! + TRACKING_PX;
  });

  return canvas.toDataURL("image/png");
}

/** `ctx.roundRect` is newer than some of the browsers this runs in. */
function roundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}
